import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconCalendarOutline, IconPercent } from '@iwoca/icons';
import { fetchPostOffersSelect } from '@iwoca/lapi-client/edge';

import OfferCard from './OfferCard';
import styles from './Offers.module.css';
import { getDurationInYears } from '../../helpers/product.helpers';
import { useContextStateKey } from '../../hooks/useContextStateKey';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { useSignupParams } from '../../hooks/useSignupParams';
import { useSignupRouterUK } from '../../hooks/useSignupRouter/useSignupRouterUK';
import { TGetOffersResponse } from '../../types';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';
import { Button } from '../Button/Button';

const ConfirmedOfferCard = ({
  max_amount,
  interest_rate,
  product_type,
  fee_percentage,
  offer_id,
  duration,
}: Pick<
  TGetOffersResponse['data']['offers'][number],
  | 'max_amount'
  | 'interest_rate'
  | 'offer_id'
  | 'product_type'
  | 'fee_percentage'
  | 'duration'
>) => {
  const { formatPercentage } = useNumberFormat();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stateKey = useContextStateKey();
  const { navigateSignup } = useSignupRouterUK();
  const { referredOfferId } = useSignupParams();

  const handleClick = async () => {
    setIsLoading(true);
    setError(null);

    if (!stateKey) {
      return null;
    }

    tracking.ampli.instantDecisionCtaClicked({
      ...getAmplitudeDefaultEventProperties(),
      offer_id,
      referred_offer_id: referredOfferId ?? undefined,
    });

    try {
      await fetchPostOffersSelect({ offerId: offer_id });
      await navigateSignup({ route: 'decision' });
    } catch {
      setError('Something went wrong, please try again.');
    }

    setIsLoading(false);
  };

  const durationLength =
    getDurationInYears(duration) > 1
      ? `${getDurationInYears(duration)} years`
      : `${getDurationInYears(duration)} year`;

  return (
    <OfferCard
      confirmedOffer
      amount={max_amount}
      productDetails={
        <ul>
          <li className={styles.listContainer}>
            <IconCalendarOutline
              data-testid="calendarOutline-icon"
              width={20}
              height={20}
              className={styles.listIcon}
            />
            Up to {durationLength}
          </li>
          <li className={styles.listContainer}>
            <IconPercent
              data-testid="percent-icon"
              width={20}
              height={20}
              className={styles.listIcon}
            />
            Monthly interest: <span>{formatPercentage(interest_rate)}</span>
          </li>
          {fee_percentage ? (
            <li className={styles.listContainer}>
              <IconPercent
                data-testid="percent-icon"
                width={20}
                height={20}
                className={styles.listIcon}
              />
              Withdrawal fee:{' '}
              <span>{formatPercentage(fee_percentage / 100)}</span>
            </li>
          ) : null}
        </ul>
      }
      button={
        <Button
          data-testid={`accept-confirmed-${product_type}`}
          onClick={handleClick}
          loading={isLoading}
          variant="secondary"
          size="small"
        >
          Proceed with this offer
        </Button>
      }
      error={error}
    />
  );
};

export default ConfirmedOfferCard;
